import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../config/react-query.config';
import { organizationService, userService, teamService } from '../../../services';
import { Team } from '../../types';

/**
 * Fetch all teams for the current user
 *
 * Features:
 * - Automatic deduplication (multiple components = 1 API call)
 * - 30 second cache (configurable in react-query.config.ts)
 * - Automatic refetch on window focus
 * - Only runs when user is authenticated (prevents 401 errors on login)
 */
export function useUserTeams() {
  // Check if user has a valid token before enabling the query
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('accessToken');

  return useQuery({
    queryKey: queryKeys.users.teams(),
    queryFn: async () => {
      const response = await userService.getUserTeamsWithMetadata();
      // Transform teams to ensure compatibility
      const teams = response.data || [];
      return teams.map((team: any) => ({
        ...team,
        id: team._id || team.id,
        createdAt: team.createdAt instanceof Date ? team.createdAt.toISOString() : (team.createdAt || ''),
        updatedAt: team.updatedAt instanceof Date ? team.updatedAt.toISOString() : (team.updatedAt || ''),
      }));
    },
    enabled: hasToken, // Only fetch when token is available
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Fetch teams for a specific organization
 *
 * This is the KEY hook that will eliminate duplicate team fetching.
 * Multiple components calling this with the same orgId will share a single API call.
 *
 * @param organizationId - The organization ID
 */
export function useOrganizationTeams(organizationId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.organizations.teams(organizationId!),
    queryFn: async () => {
      const response = await organizationService.listOrganizationTeams(organizationId!);
      // Transform teams to ensure compatibility
      return response.map((team: any) => ({
        ...team,
        id: team._id || team.id,
        createdAt: team.createdAt instanceof Date ? team.createdAt.toISOString() : (team.createdAt || ''),
        updatedAt: team.updatedAt instanceof Date ? team.updatedAt.toISOString() : (team.updatedAt || ''),
      }));
    },
    enabled: !!organizationId, // Only fetch if organizationId exists
    staleTime: 30000,
  });
}

/**
 * Fetch a single team by ID
 *
 * @param teamId - The team ID to fetch
 */
export function useTeam(teamId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.teams.detail(teamId!),
    queryFn: () => teamService.getTeamById(teamId!, ''),
    enabled: !!teamId,
    staleTime: 30000,
  });
}

/**
 * Fetch members for a specific team
 *
 * @param teamId - The team ID
 */
export function useTeamMembers(teamId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.teams.members(teamId!),
    queryFn: () => teamService.getTeamMembers(teamId!),
    enabled: !!teamId,
    staleTime: 30000,
  });
}

/**
 * Fetch content for a specific team
 *
 * @param teamId - The team ID
 */
export function useTeamContent(teamId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.teams.content(teamId!),
    queryFn: async () => {
      // This would call your content service with team filter
      // For now, returning empty array as placeholder
      return [];
    },
    enabled: !!teamId,
    staleTime: 30000,
  });
}

/**
 * Create a new team
 *
 * Automatically invalidates the organization teams cache on success
 */
export function useCreateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; description?: string; organizationId: string }) =>
      teamService.createTeam(data),
    onSuccess: (_, variables) => {
      // Invalidate organization teams and user teams
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.teams(variables.organizationId)
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.teams() });
    },
  });
}

/**
 * Update an existing team
 *
 * Automatically invalidates the team cache on success
 */
export function useUpdateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Team> }) =>
      teamService.updateTeam(id, data),
    onSuccess: (result, variables) => {
      // Invalidate specific team, organization teams, and user teams
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(variables.id) });

      // If we know the organization ID, invalidate its teams
      if (result.organizationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.organizations.teams(result.organizationId)
        });
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.users.teams() });
    },
  });
}

/**
 * Delete a team
 *
 * Automatically invalidates the teams cache on success
 */
export function useDeleteTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => teamService.deleteTeam(id),
    onSuccess: () => {
      // Invalidate all team-related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.teams() });
      // Also invalidate organization teams (we don't know which org, so invalidate all)
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all() });
    },
  });
}
